import {TooltipBulletStyle, TooltipItemType, TooltipSection} from 'common/components/chart-tooltip/types';
import {IChartDataItem} from 'common/components/line-chart/types';
import {formatDate} from 'common/util/date';
import {commaizeNumber, htmlEncodeString} from 'common/util';
import {getFunnelTrendValueGetter} from 'common/report/models/funnels/trends';
import {
  BasicFunnelStep,
  DateKeyedFunnelSegments,
  SelectMode,
  TrendChartType,
  TrendUnit,
} from 'common/report/models/funnels/types';
import {FunnelsQuery} from 'common/report/models/funnels/funnels-query';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {getRenamedOrderedSegmentSteps} from 'common/report/models/funnels/utils';
import {getSelectedSegments} from 'common/report/util/funnels/utils';
import {LineStyle} from 'common/components/line-chart/enums';

function getStepLabel(step: BasicFunnelStep): string {
  return step.step_label || step.event;
}

export function formatYAxisLabel(value: number, trendType: TrendChartType): string {
  const formatValue = getFunnelTrendValueGetter(trendType).formatValue;
  return formatValue(value, true);
}

export function formatXAxisLabel(value: number, unit: TrendUnit): string {
  const customFormatting = {day: `ddd, MMM D`};
  const displayRangeIfWeek = false;
  return formatDate(value, {unit, displayRangeIfWeek, customFormatting});
}

function formatTime(timestamp: number, unit: TrendUnit): string {
  const customFormatting = {day: `ddd, MMM D`};
  return formatDate(timestamp, {unit, customFormatting});
}

export function formatTooltip({
  x,
  series,
  comparedTrends,
  eventIndex,
  funnelsQuery,
  isCompact,
  orderedTrends,
  trendType,
}: {
  x: number;
  series: any;
  comparedTrends: DateKeyedFunnelSegments[];
  eventIndex: null | number;
  funnelsQuery: FunnelsQuery;
  isCompact: boolean;
  orderedTrends: DateKeyedFunnelSegments[];
  trendType: TrendChartType;
}): string {
  const seriesLabel = series.options.label;
  const matchingTrendIndex = orderedTrends.findIndex((bucket) => x === bucket.date.getTime());
  const matchingTrend = orderedTrends[matchingTrendIndex].segments.find((segment) => seriesLabel === segment.prop);
  const displayName = matchingTrend.propName;
  let compareSteps;
  let comparisonDate;
  if (comparedTrends?.length > 0) {
    compareSteps = comparedTrends[matchingTrendIndex].segments.find((segment) => seriesLabel === segment.prop).steps;
    comparisonDate = comparedTrends[matchingTrendIndex].date;
  }
  return _formatTooltip(
    orderedTrends[matchingTrendIndex].date.getTime(),
    displayName,
    series.color,
    funnelsQuery.trendUnit,
    trendType,
    eventIndex,
    matchingTrend.steps,
    compareSteps,
    comparisonDate,
    isCompact,
  );
}

/** formats tooltip based on if there is comparison values or not */
function _formatTooltip(
  date: number,
  displayName: string,
  color: string,
  trendUnit: TrendUnit,
  trendTypeSelection: TrendChartType,
  eventIndex: number,
  steps: BasicFunnelStep[],
  compareSteps?: BasicFunnelStep[],
  comparisonDate?: number,
  isCompact?: boolean,
): string {
  // Highcharts callbacks don't work with arrow functions
  const formattedDate = formatTime(date, trendUnit);
  let sections: TooltipSection[] = [];
  let toolTipAttrs = ``;
  if (compareSteps) {
    sections = [
      {
        rows: [
          {
            items: [
              {
                text: displayName,
                type: TooltipItemType.Strong,
              },
            ],
          },
        ],
      },
      {
        bulletStyle: TooltipBulletStyle.Solid,
        bulletColor: color,
        rows: getToolTipDataRows(steps, eventIndex, trendTypeSelection, formattedDate),
      },
      {}, // spacing
      {
        bulletStyle: TooltipBulletStyle.Dash,
        bulletColor: color,
        rows: getToolTipDataRows(compareSteps, eventIndex, trendTypeSelection, formatTime(comparisonDate, trendUnit)),
      },
    ];
    toolTipAttrs = `sections="${htmlEncodeString(JSON.stringify(sections))}"`;
  } else {
    sections = [
      {
        rows: [
          {
            items: [
              {
                text: displayName,
                type: TooltipItemType.Strong,
              },
            ],
          },
          {
            items: [
              {
                text: formattedDate,
                type: TooltipItemType.Detail,
              },
            ],
          },
        ],
      },
      {rows: getToolTipDataRows(steps, eventIndex, trendTypeSelection)},
    ];

    const isCompactAttr = isCompact ? `is-compact` : ``;
    toolTipAttrs = `title-color="${color}" ${isCompactAttr} sections="${htmlEncodeString(JSON.stringify(sections))}"`;
  }
  return `
    <mp-chart-tooltip ${toolTipAttrs}>
  `;
}

/** returns data values per tooltip based on trendType. Also formats dates if included */
function getToolTipDataRows(
  steps: BasicFunnelStep[],
  eventIndex: null | number,
  trendTypeSelection: TrendChartType,
  formattedDate: string = null,
) {
  const rows = [];
  const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
  const step = trendValueGetter.getStep(steps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
  const absoluteValue = step.count;
  const trendsValue = trendValueGetter.getTrendValue(step, eventIndex);
  const formattedTrendsValue = trendValueGetter.formatValue(trendsValue);
  if (formattedDate) {
    rows.push({
      items: [
        {
          text: formattedDate,
          type: TooltipItemType.Detail,
        },
      ],
    });
  }
  switch (trendTypeSelection) {
    case TrendChartType.Conversion:
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `conversion`, type: TooltipItemType.Text},
        ],
      });
      break;
    case TrendChartType.TimeToConvert:
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `average time to convert`, type: TooltipItemType.Text},
        ],
      });

      break;
    case TrendChartType.TopOfFunnel: {
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `entered`, type: TooltipItemType.Text},
          {text: getStepLabel(step), type: TooltipItemType.Data},
        ],
      });

      break;
    }
    case TrendChartType.BottomOfFunnel: {
      rows.push({
        items: [
          {text: formattedTrendsValue, type: TooltipItemType.Data},
          {text: `reached`, type: TooltipItemType.Text},
          {text: getStepLabel(step), type: TooltipItemType.Data},
        ],
      });

      break;
    }
  }
  if (trendTypeSelection !== TrendChartType.TopOfFunnel && trendTypeSelection !== TrendChartType.BottomOfFunnel) {
    rows.push({
      items: [
        {text: `${commaizeNumber(absoluteValue)}`, type: TooltipItemType.Data},
        {text: `total conversions`, type: TooltipItemType.Text},
      ],
    });
  }
  return rows;
}

export function getChartData({
  comparedTrends,
  eventIndex,
  funnelsQuery,
  orderedTrends,
  selected,
  selectMode,
  trendType,
}: {
  comparedTrends: DateKeyedFunnelSegments[];
  eventIndex: null | number;
  funnelsQuery: FunnelsQuery;
  orderedTrends: DateKeyedFunnelSegments[];
  selected: ColorChoice[];
  selectMode: SelectMode;
  trendType: TrendChartType;
}): IChartDataItem[] {
  const segments = getRenamedOrderedSegmentSteps(funnelsQuery, orderedTrends);
  const selectedSeriesColors = new Map(
    getSelectedSegments(selected, selectMode, segments).map<[string, string]>((s) => [s.key, s.color]),
  );

  const chartData = _getChartData({
    eventIndex,
    orderedTrends,
    selectedSeriesColors,
    trendTypeSelection: trendType,
  });

  let compareData = [];
  if (comparedTrends?.length > 0) {
    // we want to maintain the original x cooridinates so we have to replace the comparison dates before feeding to chart data
    const comparedSegments = comparedTrends.map((segment, index) => {
      return {date: orderedTrends[index].date, segments: segment.segments};
    });
    compareData = _getChartData({
      eventIndex,
      orderedTrends: comparedSegments,
      selectedSeriesColors,
      trendTypeSelection: trendType,
      dashStyle: LineStyle.Comparison,
    });
  }
  return [...chartData, ...compareData];
}

function _getChartData({
  eventIndex,
  selectedSeriesColors,
  orderedTrends,
  trendTypeSelection,
  dashStyle,
}: {
  eventIndex: null | number;
  selectedSeriesColors: Map<string, string>;
  orderedTrends: DateKeyedFunnelSegments[];
  trendTypeSelection: TrendChartType;
  dashStyle?: string;
}): IChartDataItem[] {
  return orderedTrends[0].segments.reduce((selectedSegments, {prop, propName}, segmentIndex) => {
    if (!selectedSeriesColors.has(prop)) {
      return selectedSegments;
    }
    const segmentData = orderedTrends.map(({date, segments}) => {
      const timestamp = date.getTime();
      const steps = segments[segmentIndex].steps;
      const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
      const step = trendValueGetter.getStep(steps, Number.isInteger(eventIndex) ? eventIndex - 1 : eventIndex);
      return [timestamp, trendValueGetter.getTrendValue(step, eventIndex)];
    });
    selectedSegments.push({
      color: selectedSeriesColors.get(prop),
      name: propName,
      label: prop,
      data: segmentData,
      dashStyle,
      visible: true,
    });
    return selectedSegments;
  }, []);
}
